import type { Event } from '@cocoonspace/sdk-js/types/event'
import type { SimulateEntry } from '@cocoonspace/sdk-js/types/simulateEntry'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { Trans, useTranslation } from 'next-i18next'
import { useMemo } from 'react'
import { Price } from '~/components/price'

import { InfoPopover } from '~/components/atoms/info-popover'

export interface FooterPriceUpdateProps {
	bookingSimulation: NonNullable<SimulateEntry>
	event: Event
}

export const FooterPriceUpdate = ({
	bookingSimulation,
	event,
}: FooterPriceUpdateProps) => {
	const { t } = useTranslation()

	const durationDelta = useMemo(() => {
		let sign = ''
		let title = ''

		const eventDuration =
			event.start && event.end
				? dateTimeUtils.utils.getTimeAsMinutes(event.end) -
					dateTimeUtils.utils.getTimeAsMinutes(event.start)
				: 0

		const simulationDuration =
			bookingSimulation?.start && bookingSimulation?.end
				? dateTimeUtils.utils.getTimeAsMinutes(bookingSimulation?.end) -
					dateTimeUtils.utils.getTimeAsMinutes(bookingSimulation?.start)
				: 0

		const diff = simulationDuration - eventDuration
		const timeDiff = Math.abs(diff)

		const duration =
			timeDiff === 30
				? t('common:halfHour')
				: dateTimeUtils
						.duration(timeDiff, 'minutes')
						.format('H[h]mm')
						.replace('h00', 'h')

		if (diff === 0) {
			sign = 'equal'
			title = 'booking:stepper.mode.update.tooltip.noCharges'
		} else {
			sign = diff > 0 ? 'pos' : 'neg'

			title =
				diff > 0
					? 'booking:stepper.mode.update.tooltip.addingTime'
					: 'booking:stepper.mode.update.tooltip.removingTime'
		}

		return {
			duration,
			sign,
			title,
		}
	}, [event, bookingSimulation, t])

	return (
		<div className='min-h-[45px]'>
			<div className='flex flex-row gap-2'>
				<div className='text-sm'>
					{durationDelta.sign === 'neg'
						? t('booking:stepper.mode.update.durationGap.negative', {
								duration: durationDelta.duration,
								interpolation: { escapeValue: false },
							})
						: t('booking:stepper.mode.update.durationGap.positive', {
								duration: durationDelta.duration,
								interpolation: { escapeValue: false },
							})}
				</div>

				<InfoPopover>
					<Trans
						i18nKey={durationDelta.title}
						values={{ duration: durationDelta.duration }}
						shouldUnescape
					/>
				</InfoPopover>
			</div>

			{bookingSimulation?.price_delta === 0 ? (
				<div className='font-bold'>{t('common:free')}</div>
			) : (
				<Price
					price={bookingSimulation?.price_delta}
					taxType='ht'
					taxClassName='mb-px text-xxs font-normal'
				/>
			)}
		</div>
	)
}
